"use strict";
Object.defineProperty(exports, Symbol.toStringTag, { value: "Module" });
const store = require("@tanstack/store");
const asyncRetryer = require("./async-retryer.cjs");
const utils = require("./utils.cjs");
const eventClient = require("./event-client.cjs");
function getDefaultAsyncRateLimiterState() {
  return {
    errorCount: 0,
    executionTimes: [],
    isExceeded: false,
    isExecuting: false,
    lastResult: void 0,
    maybeExecuteCount: 0,
    rejectionCount: 0,
    settleCount: 0,
    status: "idle",
    successCount: 0
  };
}
function asyncRateLimiterOptions(options) {
  return options;
}
const defaultOptions = {
  asyncRetryerOptions: {
    maxAttempts: 1
  },
  enabled: true,
  limit: 1,
  window: 0,
  windowType: "fixed",
  throwOnError: true
};
class AsyncRateLimiter {
  constructor(fn, initialOptions) {
    this.fn = fn;
    this.store = new store.Store(getDefaultAsyncRateLimiterState());
    this.asyncRetryers = /* @__PURE__ */ new Map();
    this.#timeoutIds = /* @__PURE__ */ new Set();
    this._emit = () => eventClient.emitChange("AsyncRateLimiter", this);
    this.setOptions = (newOptions) => {
      this.options = { ...this.options, ...newOptions };
    };
    this.#setState = (newState) => {
      this.store.setState((state) => {
        const combinedState = {
          ...state,
          ...newState
        };
        const isExceeded = combinedState.executionTimes.length >= this.#getLimit();
        const status = !this.#getEnabled() ? "disabled" : combinedState.isExecuting ? "executing" : isExceeded ? "exceeded" : "idle";
        return {
          ...combinedState,
          isExceeded,
          status
        };
      });
      eventClient.emitChange("AsyncRateLimiter", this);
    };
    this.#getEnabled = () => {
      return !!utils.parseFunctionOrValue(this.options.enabled, this);
    };
    this.#getLimit = () => {
      return utils.parseFunctionOrValue(this.options.limit, this);
    };
    this.#getWindow = () => {
      return utils.parseFunctionOrValue(this.options.window, this);
    };
    this.maybeExecute = async (...args) => {
      this.#setState({
        maybeExecuteCount: this.store.state.maybeExecuteCount + 1
      });
      this.#cleanupOldExecutions();
      const relevantExecutionTimes = this.#getExecutionTimesInWindow();
      if (relevantExecutionTimes.length < this.#getLimit()) {
        await this.#execute(...args);
        return this.store.state.lastResult;
      }
      this.#setState({
        rejectionCount: this.store.state.rejectionCount + 1
      });
      this.options.onReject?.(args, this);
      return void 0;
    };
    this.#execute = async (...args) => {
      if (!this.#getEnabled()) return;
      const currentMaybeExecute = this.store.state.maybeExecuteCount;
      const now = Date.now();
      const executionTimes = [...this.store.state.executionTimes, now];
      this.#setState({
        isExecuting: true,
        executionTimes
      });
      try {
        const currentAsyncRetryer = new asyncRetryer.AsyncRetryer(this.fn, {
          ...this.options.asyncRetryerOptions,
          key: `${this.key}-retryer-${currentMaybeExecute}`
        });
        this.asyncRetryers.set(currentMaybeExecute, currentAsyncRetryer);
        const result = await currentAsyncRetryer.execute(...args);
        this.#setCleanupTimeout(now);
        this.#setState({
          successCount: this.store.state.successCount + 1,
          lastResult: result
        });
        this.options.onSuccess?.(result, args, this);
      } catch (error) {
        this.#setState({
          errorCount: this.store.state.errorCount + 1
        });
        this.options.onError?.(error, args, this);
        if (this.options.throwOnError) {
          throw error;
        }
      } finally {
        this.asyncRetryers.delete(currentMaybeExecute);
        this.#setState({
          isExecuting: false,
          settleCount: this.store.state.settleCount + 1
        });
        this.options.onSettled?.(args, this);
      }
      return this.store.state.lastResult;
    };
    this.#getExecutionTimesInWindow = () => {
      if (this.options.windowType === "sliding") {
        return this.store.state.executionTimes.filter(
          (time) => time > Date.now() - this.#getWindow()
        );
      } else {
        if (this.store.state.executionTimes.length === 0) {
          return [];
        }
        const oldestExecution = Math.min(...this.store.state.executionTimes);
        const windowStart = oldestExecution;
        const windowEnd = windowStart + this.#getWindow();
        const now = Date.now();
        if (now > windowEnd) {
          return [];
        }
        return this.store.state.executionTimes.filter(
          (time) => time >= windowStart && time <= windowEnd
        );
      }
    };
    this.#setCleanupTimeout = (executionTime) => {
      if (this.options.windowType === "sliding" || this.#timeoutIds.size === 0) {
        const now = Date.now();
        const timeUntilExpiration = executionTime - now + this.#getWindow() + 1;
        const timeoutId = setTimeout(() => {
          this.#cleanupOldExecutions();
          this.#clearTimeout(timeoutId);
        }, timeUntilExpiration);
        this.#timeoutIds.add(timeoutId);
      }
    };
    this.#clearTimeout = (timeoutId) => {
      clearTimeout(timeoutId);
      this.#timeoutIds.delete(timeoutId);
    };
    this.#clearTimeouts = () => {
      this.#timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
      this.#timeoutIds.clear();
    };
    this.#cleanupOldExecutions = () => {
      this.#setState({
        executionTimes: this.#getExecutionTimesInWindow()
      });
    };
    this.getRemainingInWindow = () => {
      const relevantExecutionTimes = this.#getExecutionTimesInWindow();
      return Math.max(0, this.#getLimit() - relevantExecutionTimes.length);
    };
    this.getMsUntilNextWindow = () => {
      if (this.getRemainingInWindow() > 0) {
        return 0;
      }
      const oldestExecution = this.store.state.executionTimes[0] ?? Infinity;
      return oldestExecution + this.#getWindow() - Date.now();
    };
    this.abort = () => {
      this.asyncRetryers.forEach((retryer) => retryer.abort());
      this.asyncRetryers.clear();
      this.#setState({
        isExecuting: false
      });
    };
    this.reset = () => {
      this.#setState(getDefaultAsyncRateLimiterState());
      this.#clearTimeouts();
      this.asyncRetryers.forEach((retryer) => retryer.reset());
    };
    this.key = utils.createKey(initialOptions.key);
    this.options = {
      ...defaultOptions,
      ...initialOptions,
      throwOnError: initialOptions.throwOnError ?? !initialOptions.onError
    };
    this.#setState(this.options.initialState ?? {});
    for (const executionTime of this.#getExecutionTimesInWindow()) {
      this.#setCleanupTimeout(executionTime);
    }
    eventClient.pacerEventClient.on("d-AsyncRateLimiter", (event) => {
      if (event.payload.key !== this.key) return;
      this.#setState(event.payload.store.state);
      this.setOptions(event.payload.options);
    });
  }
  #timeoutIds;
  #setState;
  #getEnabled;
  #getLimit;
  #getWindow;
  #execute;
  #getExecutionTimesInWindow;
  #setCleanupTimeout;
  #clearTimeout;
  #clearTimeouts;
  #cleanupOldExecutions;
  /**
   * Returns the AbortSignal for a specific execution.
   * If no maybeExecuteCount is provided, returns the signal for the most recent execution.
   * Returns null if no execution is found or not currently executing.
   *
   * @param maybeExecuteCount - Optional specific execution to get signal for
   * @example
   * ```typescript
   * const rateLimiter = new AsyncRateLimiter(
   *   async (userId: string) => {
   *     const signal = rateLimiter.getAbortSignal()
   *     if (signal) {
   *       const response = await fetch(`/api/users/${userId}`, { signal })
   *       return response.json()
   *     }
   *   },
   *   { limit: 5, window: 1000 }
   * )
   * ```
   */
  getAbortSignal(maybeExecuteCount) {
    const count = maybeExecuteCount ?? this.store.state.maybeExecuteCount;
    const retryer = this.asyncRetryers.get(count);
    return retryer?.getAbortSignal() ?? null;
  }
}
function asyncRateLimit(fn, initialOptions) {
  const rateLimiter = new AsyncRateLimiter(fn, initialOptions);
  return rateLimiter.maybeExecute;
}
exports.AsyncRateLimiter = AsyncRateLimiter;
exports.asyncRateLimit = asyncRateLimit;
exports.asyncRateLimiterOptions = asyncRateLimiterOptions;
//# sourceMappingURL=async-rate-limiter.cjs.map
